import type { CivilianActivity, CivilianDefinition, CivilianStop, CivilianWalk, Point } from '../../shared/game/world-types.js';
import { emberCoastNpcs } from './ember-coast-npcs.js';

/**
 * The Ember Coast's residents: its named people (`ember-coast-npcs.ts`) where the story puts them (`docs/lore/ember-coast-pitch.md`), and the
 * unnamed folk round them: the wayfarers stranded at Pilgrim's Crossing, Wrack Cove's fishers, Greywake's survivors in the Hollow, mourners at
 * Saint's Rest, the mill's hands, the Dark Claw's captives at East Watch and the lodge's poachers. Greywake itself stays empty. Some walk a
 * round between stops (`CivilianWalk`), as patrols do. Presentation only: no rules read these, and they keep clear of what blocks.
 */
type Look = CivilianDefinition['appearance'];

/** The way to face from `from` to look at `to` (a figure faces +z at rotation 0). */
const toward = (from: Point, to: Point) => Math.atan2(to.x - from.x, to.z - from.z);
const npcLook = (id: string): Look => {
  const npc = emberCoastNpcs.find(candidate => candidate.id === id);
  if (!npc) throw new Error(`No Ember Coast NPC "${id}".`);
  return npc.appearance;
};
/** A named person standing at `at`, facing `rotation` (or toward a point), busy with `activity`. */
function named(id: string, at: Point, facing: number | Point, activity: CivilianActivity, phaseSeconds = 0): CivilianDefinition {
  const npc = emberCoastNpcs.find(candidate => candidate.id === id)!;
  return { id, name: npc.name, x: at.x, z: at.z, rotation: typeof facing === 'number' ? facing : toward(at, facing), activity, phaseSeconds, appearance: npcLook(id) };
}

// Everyday colours for the unnamed, pulled down to the world's earthy grade like the named people's.
const CLOTH = [0x6b5a45, 0x5a4e3c, 0x4e5a48, 0x6a4a3a, 0x5d4f5c, 0x485466, 0x7a6a50, 0x5e3a30, 0x3f4a3c, 0x6e5e4c, 0x58504a, 0x7c6248, 0x4f4438, 0x655a4a];
const ACCENT = [0xa59c88, 0xa89e88, 0x9a8f78, 0x8f8a7c];
const LEATHER = [0x4a3a2e, 0x5a4330, 0x3e342c, 0x51402e, 0x614537];
const SKIN = [0xe0b59a, 0xd9b19a, 0xc8957a, 0xb98a60, 0xb08a66, 0x9a6b4a, 0x865c44, 0x6e4630, 0x5e3b28, 0xe6c2a8, 0xcfa483, 0xbd8966];
const HAIR = [0x2b2725, 0x3b2823, 0x49342a, 0x5a3424, 0x6b625a, 0x8b7a5c, 0xa89a7c, 0x2e221c, 0x5b2e1e, 0x655044];
const GREY = [0x8c8780, 0xb3aea6, 0x9a968f, 0xa8a6a2];
const FACES = ['standard', 'angular', 'broad', 'soft', 'aquiline', 'gaunt', 'youthful'];
const EYES = ['standard', 'round', 'narrow', 'hooded', 'upturned', 'downturned'];
const NOSES = ['standard', 'button', 'broad', 'narrow', 'hooked', 'upturned'];
const MOUTHS = ['standard', 'full', 'thin', 'wide', 'petite', 'bowed'];
const BROWS = ['standard', 'arched', 'angled', 'thick', 'fine'];
const MALE_HAIR = ['crop', 'buzz', 'shaved', 'swept', 'tail', 'long', 'standard', 'locks'];
const FEMALE_HAIR = ['bob', 'braid', 'topknot', 'long', 'tail', 'halfup', 'crop', 'twin'];
const BEARDS = ['none', 'stubble', 'moustache', 'goatee', 'short', 'full', 'long'];

/** Who the unnamed are, which picks their clothes, wear and how they carry themselves. */
type Folk = 'wayfarer' | 'pilgrim' | 'fisher' | 'survivor' | 'mourner' | 'farmhand' | 'captive' | 'poacher';
const GARMENTS: Record<Folk, { male: string[]; female: string[] }> = {
  wayfarer: { male: ['coat', 'jacket', 'waistcoat', 'doublet', 'tunic', 'shirt-pants'], female: ['dress', 'coat', 'shirt-skirt', 'kirtle', 'waistcoat'] },
  pilgrim: { male: ['robe', 'coat', 'tunic'], female: ['robe', 'kirtle', 'dress'] },
  fisher: { male: ['shirt-pants', 'jerkin', 'tunic', 'jacket'], female: ['kirtle', 'shirt-skirt', 'short-dress'] },
  survivor: { male: ['tunic', 'shirt-pants', 'waistcoat'], female: ['kirtle', 'shirt-skirt', 'dress'] },
  mourner: { male: ['tunic', 'coat', 'robe'], female: ['kirtle', 'dress'] },
  farmhand: { male: ['shirt-pants', 'waistcoat', 'tunic'], female: ['shirt-skirt', 'kirtle'] },
  captive: { male: ['tunic', 'shirt-pants'], female: ['kirtle', 'shirt-skirt'] },
  poacher: { male: ['jerkin', 'jacket'], female: ['jerkin'] },
};
const WEAR: Record<Folk, string[][]> = {
  wayfarer: [['satchel'], ['pouch'], ['mantle'], ['strap', 'pouch'], []],
  pilgrim: [['mantle', 'pendant'], ['cowl'], ['pendant', 'satchel']],
  fisher: [['apron'], ['patches'], ['shawl'], []],
  survivor: [['soot', 'patches'], ['shawl', 'soot'], ['patches'], ['soot']],
  mourner: [['shawl'], ['mantle'], ['cowl']],
  farmhand: [['dust'], ['dust', 'apron'], ['dust', 'patches']],
  captive: [['dust', 'patches'], ['soot', 'patches'], ['dust']],
  poacher: [['strap', 'pouch'], ['strap']],
};
const BEARINGS: Record<Folk, string[]> = {
  wayfarer: ['neutral', 'neutral', 'apprehensive', 'weary'], pilgrim: ['weary', 'neutral'], fisher: ['neutral', 'weary', 'neutral'],
  survivor: ['weary', 'apprehensive'], mourner: ['weary'], farmhand: ['neutral', 'weary'], captive: ['weary', 'apprehensive'], poacher: ['neutral', 'aggressive'],
};

/** A seeded number from 0 to 1 for the `slot`th choice of a look, so a resident's look follows from its id alone. */
function chance(id: string, slot: number) {
  let h = 2166136261 ^ slot * 374761393;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  h = Math.imul(h ^ h >>> 15, 2246822507); h = Math.imul(h ^ h >>> 13, 3266489909);
  return ((h ^ h >>> 16) >>> 0) / 4294967296;
}
const pick = <T>(list: readonly T[], id: string, slot: number) => list[Math.floor(chance(id, slot) * list.length)];
const between = (low: number, high: number, id: string, slot: number) => +(low + (high - low) * chance(id, slot)).toFixed(2);

/** An unnamed resident's look: a body, a face and everyday clothes for who they are, all chosen from the id. */
export function commonerLook(id: string, folk: Folk, body: 'male' | 'female', options: { age?: [number, number] } = {}): Look {
  const male = body === 'male', [youngest, oldest] = options.age ?? [0, .75], age = between(youngest, oldest, id, 1);
  const parts: Record<string, string> = {
    face: pick(FACES, id, 2), eyes: pick(EYES, id, 3), nose: pick(NOSES, id, 4), mouth: pick(MOUTHS, id, 5), eyebrows: pick(BROWS, id, 6),
    hair: pick(male ? MALE_HAIR : FEMALE_HAIR, id, 7),
    bearing: age > .7 && chance(id, 8) < .5 ? 'stooped' : pick(BEARINGS[folk], id, 9),
  };
  if (male) parts.beard = pick(BEARDS, id, 10);
  if (folk === 'pilgrim' && chance(id, 11) < .4) parts.headwear = 'hood';
  else if ((folk === 'fisher' || folk === 'farmhand' || folk === 'captive') && chance(id, 11) < .35) parts.headwear = 'bandana';
  else if (folk === 'wayfarer' && chance(id, 11) < .2) parts.headwear = male ? 'feathered-cap' : 'bandana';
  else if (folk === 'poacher' && chance(id, 11) < .5) parts.headwear = 'leather-cap';
  if (folk === 'poacher' || folk === 'wayfarer' && chance(id, 12) < .25) parts.cape = 'short';
  return {
    archetype: 'civilian', rig: male ? 'human-male' : 'human-female', ancestry: 'human', metal: 0x6f6c66,
    clothing: pick(GARMENTS[folk][body], id, 13), height: male ? between(1.66, 1.88, id, 14) : between(1.56, 1.74, id, 14),
    shape: { weight: between(folk === 'captive' || folk === 'survivor' ? -.7 : -.4, folk === 'captive' ? -.1 : .6, id, 15), muscle: between(-.5, folk === 'fisher' || folk === 'farmhand' ? .5 : .2, id, 16),
      frame: between(-.3, .4, id, 17), age },
    cloth: pick(CLOTH, id, 18), accent: pick(ACCENT, id, 19), leather: pick(LEATHER, id, 20), skin: pick(SKIN, id, 21),
    hair: age > .6 ? pick(GREY, id, 22) : pick(HAIR, id, 22), parts,
    accessories: pick(WEAR[folk], id, 23).map(accessory => ({ id: accessory, ...accessory === 'shawl' || accessory === 'mantle' || accessory === 'cowl' || accessory === 'apron' ? { color: pick(CLOTH, id, 24) } : {} })),
  };
}

/** An unnamed resident standing at `at`. */
function folk(id: string, name: string, kind: Folk, body: 'male' | 'female', at: Point, facing: number | Point, activity: CivilianActivity, phaseSeconds = 0, age?: [number, number]): CivilianDefinition {
  return { id, name, x: at.x, z: at.z, rotation: typeof facing === 'number' ? facing : toward(at, facing), activity, phaseSeconds, appearance: commonerLook(id, kind, body, { age }) };
}
/** A stop on a round, facing a point or a way. */
const stop = (x: number, z: number, seconds: number, facing?: number | Point, activity?: CivilianActivity): CivilianStop =>
  ({ x, z, seconds, ...facing === undefined ? {} : { rotation: typeof facing === 'number' ? facing : toward({ x, z }, facing) }, ...activity ? { activity } : {} });
/** A resident who walks a round of stops, starting at the first; `speed` is a stroll. */
function walker(resident: CivilianDefinition, stops: CivilianStop[], speed = 1, carry?: CivilianWalk['carry']): CivilianDefinition {
  return { ...resident, x: stops[0].x, z: stops[0].z, walk: { stops, speed, ...carry ? { carry } : {} } };
}

// Pilgrim's Crossing: the camp's fire, tent, seats and route table, the peddler's stall on the south road, the wayside shrine.
const fire = { x: 26, z: -15 }, stall = { x: 17, z: 13 }, shrine = { x: -13, z: -17 };
const crossing: CivilianDefinition[] = [
  named('sib', { x: 22.8, z: -11.4 }, 1.3734, 'talk'),
  named('ludo-fenn', { x: 24.3, z: -11.1 }, -1.7682, 'talk', 6),
  named('garrick', { x: 28.9, z: -11.65 }, -2.43, 'drink', 2.5),
  named('wenna-shaw', { x: 28.4, z: -17.4 }, Math.PI, 'study-map', 1),
  named('aldric', { x: -11.6, z: -14.6 }, shrine, 'pray', 3),
  // Pell keeps his stall: the counter's customers stand on its south side, he behind it.
  named('pell', { x: stall.x + .2, z: stall.z - 1 }, 0, 'talk', 4),
  folk('crossing-haggler', 'Wayfarer', 'wayfarer', 'female', { x: stall.x - .3, z: stall.z + 1.6 }, Math.PI, 'talk', 10),
  folk('crossing-fireside-1', 'Wayfarer', 'wayfarer', 'male', { x: 24.3, z: -16.4 }, fire, 'warm-hands', 2, [.3, .8]),
  folk('crossing-fireside-2', 'Wayfarer', 'wayfarer', 'female', { x: 26.3, z: -16.9 }, fire, 'warm-hands', 7),
  folk('crossing-tent', 'Wayfarer', 'wayfarer', 'male', { x: 21.4, z: -15.2 }, 2.2, 'stretch', 9),
  folk('crossing-stores', 'Wayfarer', 'wayfarer', 'male', { x: 31.6, z: -12.6 }, -2, 'drink', 11),
  folk('crossing-cart-1', 'Wayfarer', 'wayfarer', 'male', { x: 9.8, z: 18.6 }, 1.25, 'talk', 3),
  folk('crossing-cart-2', 'Wayfarer', 'wayfarer', 'female', { x: 11.4, z: 18.9 }, -1.9, 'talk', 9),
  folk('crossing-milestone-1', 'Pilgrim', 'pilgrim', 'male', { x: 28.2, z: 4.4 }, -1.6, 'talk', 1, [.4, .9]),
  folk('crossing-milestone-2', 'Pilgrim', 'pilgrim', 'female', { x: 26.6, z: 4.6 }, 1.5, 'talk', 7),
  folk('crossing-shrine', 'Pilgrim', 'pilgrim', 'female', { x: -14.8, z: -14.2 }, shrine, 'pray', 8, [.3, .9]),
  folk('crossing-banner', 'Wayfarer', 'wayfarer', 'male', { x: 9.4, z: -10.2 }, -.6, 'stretch', 14),
  walker(folk('crossing-walker-1', 'Wayfarer', 'wayfarer', 'male', { x: 22, z: -8 }, 0, 'talk'),
    [stop(22, -8, 9, { x: 23.5, z: -11.2 }), stop(6, -1.5, 0), stop(16, 15.2, 12, Math.PI), stop(19, 4, 0)], 1, 'bucket'),
  walker(folk('crossing-walker-2', 'Pilgrim', 'pilgrim', 'male', { x: 9, z: -32 }, 0, 'pray', 20, [.2, .7]),
    [stop(9, -32, 6, Math.PI), stop(-2, -20, 0), stop(-10.8, -18.4, 16, shrine), stop(2, -14, 0)], .9),
  walker(folk('crossing-walker-3', 'Wayfarer', 'wayfarer', 'female', { x: 36, z: 12 }, 0, 'drink', 40),
    [stop(36, 12, 10, -1.2), stop(56, 22, 8, 1.9, 'stretch'), stop(44, 20, 0)], 1.1),
];

// Greywake's apothecary: Ilse behind her counter, the one shop still open in the burned village.
const greywake: CivilianDefinition[] = [named('ilse-varr', { x: -149.8, z: -39 }, 0, 'talk', 5)];

// Wrack Cove: Ness at the door of the one lit house, the fishers at their net racks, boats and pots, one walking the strand.
const cove: CivilianDefinition[] = [
  named('ness-harrow', { x: -300.8, z: -139.6 }, -.2, 'talk', 2),
  folk('cove-nets-1', 'Fisher', 'fisher', 'female', { x: -300, z: -173.6 }, Math.PI, 'mend', 0, [.3, .9]),
  folk('cove-nets-2', 'Fisher', 'fisher', 'male', { x: -298, z: -178.6 }, 0, 'mend', 5),
  folk('cove-nets-3', 'Fisher', 'fisher', 'male', { x: -321, z: -171.6 }, Math.PI - .5, 'mend', 3),
  folk('cove-pots', 'Fisher', 'fisher', 'male', { x: -335.6, z: -146.2 }, -2, 'mend', 7, [.4, .9]),
  folk('cove-boat', 'Fisher', 'fisher', 'female', { x: -333.5, z: -152.4 }, -1.5, 'stretch', 2),
  walker(folk('cove-walker', 'Fisher', 'fisher', 'male', { x: -282, z: -137 }, 0, 'drink'),
    [stop(-282, -137, 10, { x: -286, z: -132 }), stop(-268, -140, 0), stop(-268, -158, 0), stop(-283, -175, 0), stop(-304.4, -178.4, 14, Math.PI / 2, 'mend'), stop(-283, -175.4, 0), stop(-268.4, -157.6, 0), stop(-268.4, -140.4, 0)], .95, 'basket'),
];

// Last Light: Tamsin by the signal brazier under the lighthouse, her son Pip at the keeper's house door.
const lastLight: CivilianDefinition[] = [
  named('tamsin-hale', { x: -238.2, z: 184.4 }, { x: -240, z: 182 }, 'warm-hands', 3),
  named('pip-hale', { x: -214.4, z: 198.4 }, -Math.PI / 2, 'stretch', 6),
];

// Greymere Marsh: Nell at the reed-cutter's camp beside the abandoned hut.
const marsh: CivilianDefinition[] = [named('nell', { x: -133.6, z: 91.6 }, { x: -132, z: 90 }, 'warm-hands', 1)];

// Saint's Rest: Brother Anselm before his chapel, mourners at the graves, one carrying the dead's due back and forth.
const chapel: CivilianDefinition[] = [
  named('brother-anselm', { x: -121, z: -325.4 }, { x: -132, z: -320 }, 'talk', 4),
  folk('rest-mourner-1', 'Mourner', 'mourner', 'female', { x: -137, z: -323.6 }, Math.PI, 'pray', 2, [.3, .9]),
  folk('rest-mourner-2', 'Mourner', 'mourner', 'male', { x: -141.6, z: -317.4 }, Math.PI, 'pray', 9),
  folk('rest-digger', 'Greywake survivor', 'survivor', 'male', { x: -131, z: -311 }, Math.PI, 'dig', 0, [.1, .5]),
  walker(folk('rest-walker', 'Mourner', 'mourner', 'female', { x: -110, z: -318 }, 0, 'pray', 30),
    [stop(-110, -318, 4, -1.5), stop(-125, -311, 0), stop(-134.6, -317.4, 12, Math.PI, 'pray'), stop(-118, -313, 0)], .85),
];

// Saltgrass Mill: Hobb at his farmhouse door, a hand at the cart, another carrying between the windmill and the yard.
const mill: CivilianDefinition[] = [
  named('hobb', { x: 162, z: 349.6 }, Math.PI, 'talk', 2),
  folk('mill-cart', 'Mill hand', 'farmhand', 'male', { x: 136.4, z: 336.6 }, -2.3, 'dig', 3, [0, .5]),
  walker(folk('mill-walker', 'Mill hand', 'farmhand', 'female', { x: 144.6, z: 306 }, 0, 'stretch'),
    [stop(144.6, 306.2, 8, -1), stop(150, 330, 0), stop(149.6, 355.6, 10, Math.PI / 2, 'drink'), stop(152, 330, 0)], 1, 'basket'),
];

// East Watch: Vrisk over the captives digging at the watchtower's foot, his taskmasters' camp beside them.
const dig = { x: 586, z: 11 };
const eastWatch: CivilianDefinition[] = [
  named('vrisk', { x: 580.4, z: 6.4 }, dig, 'talk', 3),
  ...([[583, 9, 'male', 0], [586.4, 8.2, 'male', 2.4], [589.4, 10, 'female', 1.1], [584, 13.4, 'male', 3.5], [588, 14, 'female', .6]] as const)
    .map(([x, z, body, phase], i) => folk(`watch-captive-${i + 1}`, 'Captive', 'captive', body, { x, z }, dig, 'dig', phase * 1.7, [0, .6])),
];

// Copper Pine Lodge: Aud and two of her poachers.
const lodge: CivilianDefinition[] = [
  named('aud-brannoc', { x: 581, z: -34.6 }, -.6, 'talk', 1),
  folk('lodge-woodpile', 'Poacher', 'poacher', 'male', { x: 572.6, z: -32.8 }, 0, 'stretch', 4),
  folk('lodge-crates', 'Poacher', 'poacher', 'female', { x: 597.6, z: -51.6 }, -.6, 'drink', 6),
];

// Beacon Point: Esk the Ash-Saint at his signal fire.
const beacon: CivilianDefinition[] = [named('esk-ash-saint', { x: -434, z: -395.6 }, { x: -436, z: -398 }, 'warm-hands', 2)];

// The Hollow (under its roofs, on the cave floor): Greywake's survivors in the great hall round its camps and brazier, mourners in the
// sunken crypt, Bram Vell in his den, Corvin Tallow in the vault beside the hoard.
const hall = { x: 320, z: -144 };
const hollow: CivilianDefinition[] = [
  named('bram-vell', { x: 424.4, z: -296.2 }, { x: 422, z: -298 }, 'warm-hands', 2),
  named('corvin-tallow', { x: 528.6, z: -231 }, { x: 526, z: -232 }, 'talk', 4),
  folk('hollow-hall-1', 'Greywake survivor', 'survivor', 'female', { x: 318.2, z: -143 }, hall, 'warm-hands', 0, [.2, .9]),
  folk('hollow-hall-2', 'Greywake survivor', 'survivor', 'male', { x: 322, z: -145.4 }, hall, 'warm-hands', 4, [.4, .9]),
  folk('hollow-hall-3', 'Greywake survivor', 'survivor', 'male', { x: 314.4, z: -130.4 }, 1.2, 'talk', 2),
  folk('hollow-hall-4', 'Greywake survivor', 'survivor', 'female', { x: 316, z: -129.4 }, -2, 'talk', 8),
  folk('hollow-hall-5', 'Greywake survivor', 'survivor', 'female', { x: 334, z: -147.6 }, Math.PI, 'mend', 3),
  folk('hollow-hall-6', 'Greywake survivor', 'survivor', 'male', { x: 301.6, z: -148.6 }, -2.4, 'drink', 6, [.5, 1]),
  folk('hollow-crypt-1', 'Greywake survivor', 'mourner', 'female', { x: 248, z: -289 }, 0, 'pray', 1),
  folk('hollow-crypt-2', 'Greywake survivor', 'mourner', 'male', { x: 252, z: -283.6 }, Math.PI, 'pray', 6, [.4, 1]),
  walker(folk('hollow-walker', 'Greywake survivor', 'survivor', 'male', { x: 306, z: -140 }, 0, 'warm-hands', 12),
    [stop(306, -140, 10, hall, 'talk'), stop(326, -138, 0), stop(330, -152, 12, { x: 334, z: -150 }, 'warm-hands'), stop(310, -152, 0)], .9, 'bucket'),
];

export const coastCivilians: CivilianDefinition[] = [...crossing, ...greywake, ...cove, ...lastLight, ...marsh, ...chapel, ...mill, ...eastWatch, ...lodge, ...beacon, ...hollow];
